import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Box, Typography, Alert, Button, Paper, TextField, CircularProgress } from '@mui/material';

const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

const ForgotPassword = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState('form'); // 'form', 'loading', 'success', 'error'
    const [message, setMessage] = useState('');
    const [emailError, setEmailError] = useState('');

    const validateEmail = (email) => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(email) && email.length <= 254;
    };

    const handleEmailChange = (e) => {
        const newEmail = e.target.value;
        setEmail(newEmail);
        if (newEmail && !validateEmail(newEmail)) {
            setEmailError('Please enter a valid email address.');
        } else {
            setEmailError('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!email || !validateEmail(email)) {
            setEmailError('Please enter a valid email address.');
            return;
        }

        setEmailError('');
        setStatus('loading');
        setMessage('Sending recovery link...');

        try {
            const response = await axios.post(`${API_BASE_URL}/api/auth/forgot-password`, { 
                email 
            });

            setStatus('success');
            setMessage('If an account exists with this email, a password recovery link has been sent to your inbox. Please check your email (including spam folder).');
            
            // Optionally redirect after a delay
            setTimeout(() => {
                navigate('/', { replace: true });
            }, 5000);

        } catch (err) {
            setStatus('error');
            setMessage(err.response?.data?.message || 'Failed to send recovery link. Please try again.');
        }
    };

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f4f6f8', p: 2 }}>
            <Paper sx={{ p: 4, textAlign: 'center', maxWidth: 400, borderRadius: 2, boxShadow: 3 }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#ffb300' }}>
                    Forgot Password
                </Typography>

                {status === 'form' && (
                    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                        <Typography sx={{ mb: 2, color: 'text.secondary', fontSize: '0.95rem' }}>
                            Enter the email associated with your account and we'll send a recovery link.
                        </Typography>
                        
                        <TextField
                            fullWidth
                            label="Email Address"
                            type="email"
                            value={email}
                            onChange={handleEmailChange}
                            margin="normal"
                            required
                            error={!!emailError}
                            helperText={emailError}
                            placeholder="your@email.com"
                        />

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            sx={{
                                mt: 3,
                                backgroundColor: '#ffb300 !important',
                                color: '#fff',
                                fontWeight: 'bold',
                                textTransform: 'uppercase',
                                '&:hover': {
                                    backgroundColor: '#ffa000 !important',
                                }
                            }}
                        >
                            Send Recovery Link
                        </Button>

                        <Typography sx={{ mt: 2, color: 'text.secondary', fontSize: '0.9rem' }}>
                            Remember your password?{' '}
                            <Link to="/" style={{ color: '#ffb300', textDecoration: 'none', fontWeight: 'bold' }}>
                                Sign In
                            </Link>
                        </Typography>
                    </Box>
                )}

                {status === 'loading' && (
                    <Box sx={{ my: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <CircularProgress />
                        <Typography sx={{ color: 'text.secondary' }}>{message}</Typography>
                    </Box>
                )}

                {status === 'success' && (
                    <Box>
                        <Alert severity="success" sx={{ my: 2 }}>
                            {message}
                        </Alert>
                        <Button
                            variant="contained"
                            component={Link}
                            to="/"
                            fullWidth
                            sx={{
                                mt: 2,
                                backgroundColor: '#ffb300 !important',
                                color: '#fff',
                                fontWeight: 'bold',
                            }}
                        >
                            Back to Login
                        </Button>
                    </Box>
                )}

                {status === 'error' && (
                    <Box>
                        <Alert severity="error" sx={{ my: 2 }}>
                            {message}
                        </Alert>
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={() => setStatus('form')}
                            sx={{
                                mt: 2,
                                backgroundColor: '#ffb300 !important',
                                color: '#fff',
                                fontWeight: 'bold',
                            }}
                        >
                            Try Again
                        </Button>
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

export default ForgotPassword;